import axiosInstance from "./axiosConfig";
import { APIError, handleAPIError } from "./utils/errors";

/** Account emails the backend sends (through Resend) instead of Firebase. */

export type AuthEmailErrorCode =
  | "requires_recent_login"
  | "email_in_use"
  | "same_email"
  | "invalid_email"
  | "managed_by_sso"
  | "rate_limited"
  | "invalid_token"
  | "expired"
  | "no_email";

export type VerifyEmailStatus = "sent" | "already_verified";

export type EmailChangeConfirmResponse = { status: "changed"; email: string };

export type EmailChangeUndoResponse = {
  status: "restored";
  email: string;
  reset_link: string;
};

/** The backend's `code` for an account-email failure, when it sent one. */
export const authEmailErrorCode = (
  error: unknown
): AuthEmailErrorCode | null => {
  if (!(error instanceof APIError)) return null;
  const details = error.details as { code?: unknown } | undefined;
  return typeof details?.code === "string"
    ? (details.code as AuthEmailErrorCode)
    : null;
};

const call = async <T>(path: string, body: unknown): Promise<T> => {
  try {
    const response = await axiosInstance.post<T>(path, body);
    return response.data;
  } catch (error) {
    const apiError = handleAPIError(error);
    throw apiError;
  }
};

export const AuthEmailAPI = {
  /** Always resolves the same way, whether or not the account exists. */
  async requestPasswordReset(email: string): Promise<void> {
    await call("/auth/password-reset", { email });
  },

  async sendVerificationEmail(): Promise<VerifyEmailStatus> {
    const data = await call<{ status: VerifyEmailStatus }>(
      "/auth/verify-email",
      {}
    );
    return data.status;
  },

  /** Emails a confirmation link to `newEmail`; nothing changes until it's clicked. */
  async requestEmailChange(newEmail: string): Promise<string> {
    const data = await call<{ status: "sent"; email: string }>(
      "/auth/email-change",
      { new_email: newEmail }
    );
    return data.email;
  },

  confirmEmailChange(token: string): Promise<EmailChangeConfirmResponse> {
    return call("/auth/email-change/confirm", { token });
  },

  undoEmailChange(token: string): Promise<EmailChangeUndoResponse> {
    return call("/auth/email-change/undo", { token });
  }
};
